import { type User } from '@/features/account/services/User/types'
import { isOnServer } from '@shared/constants/util'
import { reset } from '@shipt/analytics-member-web'
import { type ShiptContextTraits } from '@shipt/analytics-core'
import { getGuestParamsFromCache } from '@/features/account/services/GuestUser/getGuestParamsFromCache'
import { getIsZipOnlySession, getUser, getUserZip } from '@/features/account/services/User/utils'
import { getQueryClient } from '@/features/shared/utils/dataFetching/reactQuery/SegwayQueryClient'
import { getCPAData } from '@/features/shared/utils/cpaDetection'
import { getActiveGroupId } from '@/features/business/utils/getActiveGroupId'
import { matchRouteByPath } from '@shared/utils/matchRouteByPath'
import { getDataRightsTrait } from '@/features/account/services/DataPrivacy/utils'

export const USER_TRAITS_SESSION_STORAGE = 'ajs_user_traits'

// These traits about a user are recorded using the Identify API.
// Please read the Identify API spec for information on why
// we do this: https://segment.com/docs/spec/identify/

// various customer endpoints return partial responses but we don't want to fail
/* oxlint-disable @typescript-eslint/no-unnecessary-condition */
const getUserTraits = (user: User): ShiptContextTraits => {
  const {
    id,
    name = '',
    email,
    store,
    metro,
    phone,
    marketing_phone,
    metro_id,
    store_location_id,
    order_delivery_type,
    universal_id,
    exempt,
    subscription,
    plan_type,
    do_not_share_sell_ta,
  } = user
  return {
    id,
    name,
    first_name: firstName(name),
    last_name: lastName(name),
    email,
    phone: phone ? `1${phone}` : phone,
    metro: metro?.name,
    metro_id: metro_id ?? -1,
    store: store?.name ?? '',
    store_id: store?.id ?? -1,
    store_location_id: store_location_id ?? -1,
    order_delivery_type: order_delivery_type ?? '',
    universal_id: universal_id ?? -1,
    exempt,
    plan_type: plan_type ?? undefined,
    do_not_share_sell_ta,
    ...(subscription && {
      subscription_status: subscription?.status ?? '',
      subscription_type: exempt ? 'Exempt' : (subscription?.type ?? ''),
    }),
    zip: user?.customer_addresses ? getUserZip(user) : undefined,
    ...(marketing_phone && { marketing_phone }),
  }
}
// various customer endpoints return partial responses but we don't want to fail
/* oxlint-enable @typescript-eslint/no-unnecessary-condition */

export const getGuestTrait = () => {
  const user = getUser(getQueryClient())
  const guestAddress = getGuestParamsFromCache().guestAddress
  return {
    guest: Boolean(guestAddress && !user?.id),
  }
}

export const getGroupProperties = () => {
  const user = getUser(getQueryClient())
  const groups = user?.groups
  if (!groups?.length) return {}

  const activeGroupId = getActiveGroupId({ cookies: undefined })
  if (!activeGroupId) return {}

  const activeGroup = groups.find((group) => group.id === activeGroupId)
  if (!activeGroup) return {}

  const isOwner = user?.id !== undefined && String(user.id) === activeGroup.ownerId

  return {
    groupId: activeGroup.id,
    groupRole: isOwner ? ('owner' as const) : ('purchaser' as const),
  }
}

/**
 * Gets CPA (Comet Partner Attribute) traits for Perplexity Comet browser detection
 * This adds information about whether the user is browsing from the Comet browser
 * to the Segment identify call for analytics purposes.
 *
 * @returns object containing CPA-related traits
 */
export const getCPATraits = () => {
  if (isOnServer()) {
    return {}
  }

  const cpaData = getCPAData()

  // Only include CPA data if browser is likely Comet (cpa cookie is present)
  if (!cpaData.unverified_is_comet) {
    return {}
  }

  return {
    comet_token: cpaData.comet_token,
    unverified_is_comet: cpaData.unverified_is_comet,
  }
}

const firstName = (name: string) => {
  const splittedName = name.split(' ')
  return splittedName.length > 0 ? splittedName[0] || '' : name
}
const lastName = (name: string) => {
  const splittedName = name.split(' ')
  return splittedName.length > 1 ? splittedName.at(-1) || '' : ''
}

// Data science wants us to send a location property. When a search is
// executed from the HeaderContainer, we need a way to know the search origin
export const urlPathnameToLocationName = (): string => {
  if (isOnServer()) return ''
  const routeMatch = matchRouteByPath(window.location.pathname)
  if (!routeMatch) return ''

  return routeMatch.trackingName || window.location.pathname.substring(1)
}

export const resetAnalytics = () => {
  if (shouldDisableAnalytics()) return
  reset()
}

// disable segment for cypress, lighthouse, and load tests as it contributes to high event usage and MTU costs
export const shouldDisableAnalytics = () =>
  !isOnServer() && Boolean(window.location.search.includes('disable_segment=true'))

export const getPageData = () => ({
  path: location.pathname,
  referrer: document.referrer,
  search: location.search,
  title: document.title,
  url: location.href,
})

export const getAllUserTraits = (user: User | undefined): ShiptContextTraits => ({
  ...(user && getUserTraits(user)),
  ...getDataRightsTrait(),
  ...getGuestTrait(),
  zip_only: getIsZipOnlySession(user),
})
